/**
 * @file Guards.tsx
 * @description Security route guards and access controllers for Venue Admin.
 * 
 * GUARD ARCHITECTURE:
 * 1. ProtectedRoute:
 *    - Verifies user authentication.
 *    - Blocks non-venue roles (e.g. internal platform accounts or standalone couriers) to maintain separation of concerns.
 *    - Enforces email confirmation before granting access to venue tools.
 *    - Enforces venue creation if the user has no registered venues.
 * 2. RoleGuard:
 *    - Enforces role-based access control (RBAC) on specific nested admin sections (e.g. CHEF, WAITER, OWNER, MANAGER).
 * 3. RootAuthGateway:
 *    - Intelligent landing router resolving "/" to the appropriate view based on role.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useVenues } from '../../hooks/useVenues';
import { supabase } from '../../lib/supabase';
import { AlertCircle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute: Wraps authenticated routes.
 * 
 * DEBUGGING TIPS:
 * - If user is constantly kicked back to /login, check if `profile` is null or if `profile.role`
 *   contains an internal role (e.g. `tablegreet.admin`) which is forbidden on venue-admin.
 * - If user is redirected to /register?step=confirm-email, check Supabase auth `email_confirmed_at`.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { profile, venues, authEmail, isEmailConfirmed, isLoading, activeMemberRole, orgRole } = useVenues();
  const location = useLocation();

  // 1. Loading state while security context synchronizes
  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
        <p className="text-gray-400 font-bold animate-pulse uppercase tracking-[0.2em] text-xs">Synchronizing Security Context...</p>
      </div>
    );
  }

  // 2. Unauthenticated check -> Redirect to login with return path
  if (!profile) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Security Boundary: Internal platform accounts (employees/admins) are rejected from venue-admin
  const userRole = String(profile.role || '');
  const isInternalRole = !userRole.startsWith('tablegreet.venue.') && userRole.startsWith('tablegreet.') && userRole !== 'tablegreet.consumer' && userRole !== 'tablegreet.courier';
  const isForbiddenRole = isInternalRole;

  if (isForbiddenRole) {
    void supabase.auth.signOut({ scope: 'local' });
    return <Navigate to="/login" replace />;
  }

  // 4. Email verification check
  if (isEmailConfirmed === false) {
    const emailParam = authEmail ? `&email=${encodeURIComponent(authEmail)}` : '';
    return <Navigate to={`/register?step=confirm-email${emailParam}`} replace />;
  }

  // 5. Dedicated Staff Check: Chef, waiter, and courier accounts are not permitted on the venue admin dashboard;
  // sign out locally so that specialized consoles open in other tabs are not disrupted.
  const userStaffRole = String(profile.role || '');
  const activeRoleUpper = (activeMemberRole || orgRole || '').toUpperCase();
  const isTerminalStaff = userStaffRole === 'tablegreet.venue.chef' || userStaffRole === 'tablegreet.venue.waiter' || userStaffRole === 'tablegreet.courier' ||
    activeRoleUpper === 'CHEF' || activeRoleUpper === 'WAITER' || activeRoleUpper === 'COURIER' || activeRoleUpper === 'DRIVER' || activeRoleUpper === 'DISPATCHER';

  if (isTerminalStaff && !activeRoleUpper.includes('OWNER') && !activeRoleUpper.includes('MANAGER') && !activeRoleUpper.includes('ADMIN')) {
    void supabase.auth.signOut({ scope: 'local' });
    return <Navigate to="/login" replace />;
  }

  if (!venues || venues.length === 0) {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center p-6 text-center">
        <div className="max-w-md w-full space-y-8 bg-brand-surface p-8 rounded-2xl border border-white/5 shadow-2xl">
          <div className="w-16 h-16 bg-brand-primary/10 border border-brand-primary/20 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle size={32} className="text-brand-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-white">Venue Setup Required</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              You are logged in as <span className="text-white font-semibold">{authEmail}</span> but have not created a venue yet.
            </p>
          </div>
          <div className="flex flex-col gap-3 pt-4">
            <button
              onClick={() => window.location.href = '/register?step=venue'}
              className="w-full py-4 rounded-xl bg-brand-primary text-white font-bold hover:bg-brand-primary/95 transition-all shadow-xl shadow-brand-primary/15 cursor-pointer"
            >
              Continue to Onboarding
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut({ scope: 'local' });
                window.location.href = '/login';
              }}
              className="w-full py-4 rounded-xl bg-white/5 border border-white/10 text-gray-400 font-bold hover:bg-white/10 transition-all cursor-pointer"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

export const RoleGuard: React.FC<RoleGuardProps> = ({ children, allowedRoles }) => {
  const { orgRole, activeMemberRole, isLoading } = useVenues();

  if (isLoading) return (
    <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
      <p className="text-gray-400 font-bold animate-pulse uppercase tracking-[0.2em] text-xs">Verifying Permissions...</p>
    </div>
  );

  const currentRole = (orgRole || activeMemberRole || '').toUpperCase();
  const isAllowed = allowedRoles.some(role => role.toUpperCase() === currentRole);

  if (!isAllowed) {
    console.warn(`[RoleGuard] Access denied for role: ${currentRole}. Required: ${allowedRoles.join(', ')}`);
    void supabase.auth.signOut({ scope: 'local' });
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export const RootAuthGateway: React.FC = () => {
  const { profile, venues, authEmail, isEmailConfirmed, isLoading, activeMemberRole, orgRole } = useVenues();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
        <p className="text-gray-400 font-bold animate-pulse uppercase tracking-[0.2em] text-xs">
          Verifying Session &amp; Security Context...
        </p>
      </div>
    );
  }

  if (!profile) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Security Boundary: Internal platform accounts (employees/admins) are rejected from venue-admin
  const userRole = String(profile.role || '');
  const isInternalRole = !userRole.startsWith('tablegreet.venue.') && userRole.startsWith('tablegreet.') && userRole !== 'tablegreet.consumer' && userRole !== 'tablegreet.courier';
  const isForbiddenRole = isInternalRole;

  if (isForbiddenRole) {
    void supabase.auth.signOut({ scope: 'local' });
    return <Navigate to="/login" replace />;
  }

  if (isEmailConfirmed === false) {
    const emailParam = authEmail ? `&email=${encodeURIComponent(authEmail)}` : '';
    return <Navigate to={`/register?step=confirm-email${emailParam}`} replace />;
  }

  if (!venues || venues.length === 0) {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center p-6 text-center">
        <div className="max-w-md w-full space-y-8 bg-brand-surface p-8 rounded-2xl border border-white/5 shadow-2xl">
          <div className="w-16 h-16 bg-brand-primary/10 border border-brand-primary/20 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle size={32} className="text-brand-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-white">Venue Setup Required</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              You are logged in as <span className="text-white font-semibold">{authEmail}</span> but have not created a venue yet.
            </p>
          </div>
          <div className="flex flex-col gap-3 pt-4">
            <button
              onClick={() => window.location.href = '/register?step=venue'}
              className="w-full py-4 rounded-xl bg-brand-primary text-white font-bold hover:bg-brand-primary/95 transition-all shadow-xl shadow-brand-primary/15 cursor-pointer"
            >
              Continue to Onboarding
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut({ scope: 'local' });
                window.location.href = '/login';
              }}
              className="w-full py-4 rounded-xl bg-white/5 border border-white/10 text-gray-400 font-bold hover:bg-white/10 transition-all cursor-pointer"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const userStaffRole = String(profile.role || '');
  const activeRoleUpper = (activeMemberRole || orgRole || '').toUpperCase();
  const isTerminalStaff = userStaffRole === 'tablegreet.venue.chef' || userStaffRole === 'tablegreet.venue.waiter' || userStaffRole === 'tablegreet.courier' ||
    activeRoleUpper === 'CHEF' || activeRoleUpper === 'WAITER' || activeRoleUpper === 'COURIER' || activeRoleUpper === 'DRIVER' || activeRoleUpper === 'DISPATCHER';

  if (isTerminalStaff && !activeRoleUpper.includes('OWNER') && !activeRoleUpper.includes('MANAGER') && !activeRoleUpper.includes('ADMIN')) {
    void supabase.auth.signOut({ scope: 'local' });
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/admin" replace />;
};
